import { Component, OnInit, Inject } from '@angular/core';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntypedFormControl } from '@angular/forms';
import { filter, Observable } from 'rxjs';
import { map, startWith, take } from 'rxjs/operators';
import { FilterDisplaySettings } from '../brand-filter-container.service';
import { BrandsService } from '../../brands.service';
import { Group } from '../../brands.api.service';

export interface GroupData {
  selectedGroup: GroupWithDisplayName;
  filterSettings: FilterDisplaySettings;
}

export class GroupWithDisplayName {
  constructor(
    public name: string,
    public group: Group,
  ) {}
}

@Component({
  selector: 'bc-brand-group-dialog',
  templateUrl: './brand-group-dialog.component.html',
  styleUrls: ['./brand-group-dialog.component.scss'],
  standalone: false,
})
export class BrandGroupDialogComponent implements OnInit {
  groupControl = new UntypedFormControl();
  filteredOptions$: Observable<GroupWithDisplayName[]>;
  options$: Observable<GroupWithDisplayName[]>;
  selectedGroup: GroupWithDisplayName | null;
  private originalSelectedGroup: GroupWithDisplayName | null = null;
  private options: GroupWithDisplayName[] = [];

  constructor(
    public dialogRef: MatDialogRef<BrandGroupDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: GroupData,
    private brandsService: BrandsService,
  ) {
    this.selectedGroup = data.selectedGroup ?? null;
  }

  buildGroupName(groupsByPath: Map<string, Group>, group: Group): string {
    const parent = groupsByPath.get(group.pathNodes.slice(0, -1).join('|'));
    if (!parent) {
      return group.name;
    } else {
      const parentName = this.buildGroupName(groupsByPath, parent);
      return `${parentName} / ${group.name}`;
    }
  }

  ngOnInit(): void {
    this.brandsService.allGroups$
      .pipe(
        filter((groups): groups is Group[] => !!groups), // ignore null
        take(1),
      )
      .subscribe((groups) => {
        const paths = new Map<string, Group>();
        groups.forEach((g) => paths.set(g.pathNodes.join('|'), g));

        this.options = groups
          .map((g) => new GroupWithDisplayName(this.buildGroupName(paths, g), g))
          .sort((a, b) => a.name.localeCompare(b.name));

        // Set initial selection if any
        if (this.selectedGroup) {
          this.groupControl.setValue(this.selectedGroup.name);
        }

        // Setup filtered options observable
        this.filteredOptions$ = this.groupControl.valueChanges.pipe(
          startWith(this.groupControl.value || ''),
          map((value: string) => this.filterOptions(value)),
        );
      });
  }

  private filterOptions(filterValue: string): GroupWithDisplayName[] {
    return this.options.filter((option) => option.name.includes(filterValue));
  }

  save(): void {
    if (this.selectedGroup) {
      this.originalSelectedGroup = this.selectedGroup;
      this.dialogRef.close({ selectedGroup: this.selectedGroup });
      return;
    }
    this.dialogRef.close();
  }

  cancel(): void {
    this.dialogRef.close({ selectedGroup: this.originalSelectedGroup });
  }

  setGroupOpt(event: MatAutocompleteSelectedEvent): void {
    const selected = event.option.value as GroupWithDisplayName;
    this.setGroup(selected);
  }

  private setGroup(group: GroupWithDisplayName): void {
    this.selectedGroup = group;
    this.groupControl.setValue(group?.name || '', { emitEvent: true });
  }

  clearSelection(): void {
    this.selectedGroup = null;
    this.groupControl.setValue('', { emitEvent: true });
  }
}
